/**
 * <ChatInput /> — the persistent input at the bottom of the chat
 * panel (M22).
 *
 * Enter sends; Shift+Enter inserts a newline. Disabled while a turn
 * is streaming so the user can't pile up overlapping turns.
 */

import { SendHorizonal } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  CHAT_OPEN_EVENT,
  CHAT_PREFILL_KEY,
} from "../../lib/chat-explain";

export function ChatInput({
  onSend,
  disabled,
}: {
  onSend: (text: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  // Drain any pending prefill (set by openChatPanel({prefillInput})).
  // Fires on initial mount AND every time CHAT_OPEN_EVENT dispatches
  // — the chat panel stays mounted across open/close cycles, so a
  // single mount-time useEffect would miss every prefill after the
  // first.
  useEffect(() => {
    function drain() {
      if (typeof window === "undefined") return;
      try {
        const prefill = sessionStorage.getItem(CHAT_PREFILL_KEY);
        if (!prefill) return;
        sessionStorage.removeItem(CHAT_PREFILL_KEY);
        setValue(prefill);
        requestAnimationFrame(() => {
          if (ref.current) {
            ref.current.style.height = "auto";
            ref.current.style.height = `${Math.min(
              ref.current.scrollHeight,
              260,
            )}px`;
            ref.current.focus();
            const len = ref.current.value.length;
            ref.current.setSelectionRange(len, len);
          }
        });
      } catch {
        // sessionStorage unavailable — non-fatal.
      }
    }
    drain();
    window.addEventListener(CHAT_OPEN_EVENT, drain);
    return () => window.removeEventListener(CHAT_OPEN_EVENT, drain);
  }, []);

  function submit() {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue("");
    // Reset the autogrow height.
    if (ref.current) ref.current.style.height = "auto";
  }

  return (
    <div className="border-t border-border-subtle p-3">
      <div className="flex items-end gap-2 rounded-xl border border-border-subtle bg-surface-default px-3 py-2 focus-within:border-brand/50">
        <textarea
          ref={ref}
          rows={1}
          value={value}
          disabled={disabled}
          placeholder={t("app.ci_placeholder")}
          onChange={(e) => {
            setValue(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = `${Math.min(
              e.target.scrollHeight,
              140,
            )}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          className="flex-1 resize-none bg-transparent text-sm text-text-primary outline-none placeholder:text-text-tertiary disabled:opacity-50"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || !value.trim()}
          aria-label={t("app.ci_send")}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand text-brand-fg hover:opacity-90 disabled:opacity-40"
        >
          <SendHorizonal size={14} />
        </button>
      </div>
    </div>
  );
}
